/**
 * TypeScript port of the Python conditional engine. Behavior matches TRS's conditional engine, however, there are these known gaps:
 *
 * - Booleans and numbers are distinct types. Python treats `True`/`False` as `1`/`0`, so `true EQ 1`, `false EQ 0`,
 *   `1 IN [true]`, `[1] ANYIN [true]`, and nested values such as `[true] EQ [1]` or `{ a: true } EQ { a: 1 }` are
 *   true in Python and false here, and `NE`/`NIN` are the reverse. Ordering a boolean against a number or another
 *   boolean (`true GT 0`, `true GE false`, `1 GT true`) returns a result in Python and throws here.
 * - `ANYIN` only compares two lists. A string or dict on either side throws here, where Python compares the string's
 *   characters or the dict's keys.
 */
import { isEqual } from 'lodash-es'

import {
  Condition,
  ConditionGroup,
  ConditionOperator,
  ConditionOperatorName,
  Conditions,
  ConditionsParam,
  EvaluateConditionsResult,
  ExpressionsResult,
  Operation,
  OperationName,
} from './types'

const isPlainObject = (value: unknown): value is object =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const hasKey = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key)

const isConditionGroup = (conditions: Conditions): conditions is ConditionGroup =>
  hasKey(conditions, 'condition_operator')

const isNonEmpty = (obj: ExpressionsResult | null | undefined): obj is ExpressionsResult =>
  !!obj && Object.keys(obj).length > 0

/**
 * Equivalent of the Python `BaseEnum.from_str`: converts the name of the enum (optionally prefixed with the enum's
 * name, e.g. "Operation.EQ") into the enum value, throwing if the name is not a member.
 */
const parseEnumName = <T extends string>(enumObject: Record<string, T>, enumName: string, label: string): T => {
  const name = label.startsWith(`${enumName}.`) ? label.slice(enumName.length + 1) : label
  if (!hasKey(enumObject, name)) {
    throw new Error(`Unsupported ${enumName}: ${label}`)
  }
  return enumObject[name]
}

const parseOperation = (label: OperationName): Operation => parseEnumName(Operation, 'Operation', label)

const parseConditionOperator = (label: ConditionOperatorName): ConditionOperator =>
  parseEnumName(ConditionOperator, 'ConditionOperator', label)

/**
 * Mirrors Python's ordering comparisons, which only allow comparing like types and raise a TypeError otherwise
 * (rather than coercing like JavaScript's relational operators do).
 */
const compare = (left: unknown, right: unknown): number => {
  if (
    (typeof left === 'number' && typeof right === 'number') ||
    (typeof left === 'string' && typeof right === 'string')
  ) {
    // Unordered values (NaN) compare false in every direction, like Python.
    return left < right ? -1 : left > right ? 1 : left === right ? 0 : NaN
  }
  throw new TypeError(`Cannot compare ${typeof left} with ${typeof right}`)
}

/**
 * Mirrors Python's `item in items` for lists (deep equality) and strings (substring).
 */
const isInList = (item: unknown, items: unknown): boolean => {
  if (Array.isArray(items)) {
    return items.some((listItem) => isEqual(listItem, item))
  }
  if (typeof items === 'string' && typeof item === 'string') {
    return items.includes(item)
  }
  throw new TypeError(`Cannot check if ${typeof item} is in ${typeof items}`)
}

const isScalar = (value: unknown): boolean => value === null || ['string', 'number', 'boolean'].includes(typeof value)

/**
 * Mirrors Python's `bool(set(items) & set(values))` for two lists of scalars. Anything other than a list on either
 * side throws. List or object elements throw too, as they're unhashable in Python.
 */
const isAnyInList = (items: unknown, values: unknown): boolean => {
  if (!Array.isArray(items) || !Array.isArray(values) || !items.every(isScalar) || !values.every(isScalar)) {
    throw new TypeError(`Cannot check if any of ${typeof items} is in ${typeof values}`)
  }
  return items.some((item) => values.some((value) => isEqual(item, value)))
}

const applyOperation = (operation: Operation, left: unknown, right: unknown): boolean => {
  switch (operation) {
    case Operation.EQ:
      return isEqual(left, right)
    case Operation.NE:
      return !isEqual(left, right)
    case Operation.GT:
      return compare(left, right) > 0
    case Operation.GE:
      return compare(left, right) >= 0
    case Operation.LT:
      return compare(left, right) < 0
    case Operation.LE:
      return compare(left, right) <= 0
    case Operation.IN:
      return isInList(left, right)
    case Operation.NIN:
      return !isInList(left, right)
    case Operation.ANYIN:
      return isAnyInList(left, right)
  }
}

const applyConditionOperator = (conditionOperator: ConditionOperator, values: boolean[]): boolean =>
  conditionOperator === ConditionOperator.AND ? values.every(Boolean) : values.some(Boolean)

/**
 * Given a set of conditions, apply the condition parameters against them by evaluating the results to a tuple
 * containing a boolean and an object. This engine treats the conditions as a tree data structure and recursively walks
 * down the nodes until a final aggregate boolean value is interpreted. If the final aggregate boolean result is true,
 * then the provided conditions satisfy the given condition parameters. The object in the tuple will contain
 * information extracted from the `expressions_result` field within the conditions, but only if the boolean is true
 * and the `expressions_result` exists. Otherwise, the object portion of the tuple will be an empty object.
 *
 * Note, there can be nested `expressions_result` and as a result the lowest level `expressions_result` will be
 * returned.
 *
 * @param conditions The nested conditions
 * @param conditionsParam The parameters which the conditions are evaluated against
 * @returns A tuple [boolean, object] where the boolean indicates the overall satisfaction of conditions, and the
 *   object contains relevant information based on `expressions_result` if conditions are met
 */
export const evaluateConditions = (
  conditions: Conditions,
  conditionsParam: ConditionsParam,
): EvaluateConditionsResult => {
  if (!isPlainObject(conditions)) {
    // Python raises on a list or scalar node rather than treating it as unsatisfied.
    throw new TypeError(
      `Condition node must be an object, got ${Array.isArray(conditions) ? 'array' : conditions === null ? 'null' : typeof conditions}`,
    )
  }
  if (isConditionGroup(conditions)) {
    // The current node is comprised of expressions, so we need to apply the given ConditionOperator across the
    // underlying expressions.
    const conditionOperator = parseConditionOperator(conditions.condition_operator)

    // Saving the node's expressions_result field
    let expressionsResult: ExpressionsResult = conditions.expressions_result ?? {}

    // Every expression is evaluated (no short-circuiting) so the last satisfied child's expressions_result wins
    const evaluatedExpressions: boolean[] = []
    for (const expression of conditions.expressions) {
      const [evaluatedExpression, evaluatedExpressionResult] = evaluateConditions(expression, conditionsParam)

      // Storing the expression's evaluated information
      if (isNonEmpty(evaluatedExpressionResult)) {
        expressionsResult = evaluatedExpressionResult
      }
      evaluatedExpressions.push(evaluatedExpression)
    }

    // Applying the node's ConditionOperator on the list of boolean expression results
    const isConditionSatisfied = applyConditionOperator(conditionOperator, evaluatedExpressions)

    // Returning the node's aggregated boolean result and its expressions result field
    return [isConditionSatisfied, isConditionSatisfied ? expressionsResult : {}]
  }

  // The current node is a single operation (a leaf node), we should evaluate the target to a boolean.
  if (!hasKey(conditions, 'target') || !hasKey(conditions, 'operation') || !hasKey(conditions, 'value')) {
    // Python raises a KeyError for a leaf missing any of these keys.
    throw new Error('A leaf condition must have a target, operation, and value')
  }
  const { target, operation, value }: Condition = conditions
  if (!hasKey(conditionsParam, target) || conditionsParam[target] === undefined) {
    // If the target is not in the condition parameters, early exit and return false. An undefined value counts as
    // missing, since serializing the parameters to JSON for the backend drops the key.
    return [false, {}]
  }

  // Apply the operation to the target
  return [applyOperation(parseOperation(operation), conditionsParam[target], value), {}]
}
